import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  inject
} from '@angular/core';
import { Button } from 'primeng/button';
import { AuthService } from '../services/auth.service';

function asList(value: string | string[] | null | undefined): string[] {
  if (value == null || value === '') {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/**
 * Oculta el bloque si el token no tiene el permiso (secciones de menú / paneles).
 * Uso: *appHasPermission="'CUSTOMER_LIST'" o *appHasPermission="['A','B']" (todos).
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: true
})
export class HasPermissionDirective implements OnChanges {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly auth = inject(AuthService);

  @Input('appHasPermission') permission: string | string[] | null = null;

  ngOnChanges(): void {
    this.viewContainer.clear();
    if (this.auth.hasAllRoles(asList(this.permission))) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }
}

/**
 * Deshabilita botones y enlaces si falta el permiso (no los oculta).
 * Uso: appDisableUnless="CUSTOMER_WRITE"
 */
@Directive({
  selector: '[appDisableUnless]',
  standalone: true
})
export class DisableUnlessPermissionDirective implements OnChanges {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly auth = inject(AuthService);
  private readonly primeButton = inject(Button, { optional: true, self: true });

  @Input('appDisableUnless') permission: string | string[] | null = null;

  ngOnChanges(): void {
    const blocked = !this.auth.hasAllRoles(asList(this.permission));
    const native = this.el.nativeElement;

    if (this.primeButton && blocked) {
      this.primeButton.disabled = true;
    }

    if (blocked && (native instanceof HTMLButtonElement || native instanceof HTMLInputElement)) {
      native.disabled = true;
    }

    const nativeDisabled = native instanceof HTMLButtonElement || native instanceof HTMLInputElement
      ? native.disabled
      : false;
    this.renderer.setAttribute(native, 'aria-disabled', blocked || nativeDisabled ? 'true' : 'false');
    if (blocked) {
      this.renderer.addClass(native, 'opacity-50');
      this.renderer.addClass(native, 'cursor-not-allowed');
      this.renderer.setAttribute(native, 'tabindex', '-1');
    } else {
      this.renderer.removeClass(native, 'opacity-50');
      this.renderer.removeClass(native, 'cursor-not-allowed');
      this.renderer.removeAttribute(native, 'tabindex');
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (!this.auth.hasAllRoles(asList(this.permission))) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}

export const PERMISSION_UI_DIRECTIVES = [HasPermissionDirective, DisableUnlessPermissionDirective] as const;
